class ExpandableBehavior {
    /**
     * @param {object} data - The configuration object.
     * @param {object} data.classes - the classes that are used / to be used in the HTML
     * @param {object} data.selectors - the elements that we are going to manipulate
     * @param {object} data.settings - the initialization settings
     */
    constructor(data) {
        this.classes = {
            CHEVRON: 'icon-arrow_up',
            DISABLED: 'inactive',
            EXPANDED_ICON: undefined,
            COLLAPSED_ICON: undefined,
            ...data.classes,
        };
        this.selectors = {
            CONTENT: undefined,
            LIST: [],
            TRIGGER: undefined,
            TOGGLE_ALL: '.toggle-all',
            TOGGLE_ALL_WRAPPER: undefined,
            TOGGLE_TEMPLATE: 'collapsible_authors_template',
            WRAPPER_CONTAINER: undefined,
            ...data.selectors,
        };
        this.settings = {
            ADD_MISSING_TRIGGERS: false,
            HEADING_LEVEL: 'H4',
            START_COLLAPSED: true,
            TOGGLE_ALL_POSITION: 'append',
            WITH_CHEVRON: true,
            WITH_TOGGLE_ALL: true,
            FORCE_HYDRATE_TRIGGER: false,
            ANIMATION_SPEED: 'fast',
            ACCORDION_ENABLED: false,
            ACCORDION_INSTANT_CLOSE_OTHER: true,
            IGNORE_EMPTY: true,
            ...data.settings,
        };
        this.itemIsContent;
        this.toggleAllTrigger;
        this.expandedList = [];
        this.parentWrapper = this.selectors.TOGGLE_ALL_WRAPPER || this.selectors.WRAPPER_CONTAINER;

        const updatedList = [];
        this.selectors.LIST.forEach(item => {
            if (this.settings.ADD_MISSING_TRIGGERS) {
                let trigger = item.querySelector(this.selectors.TRIGGER);
                if (!trigger) {
                    trigger = document.createElement('div');
                    trigger.classList.add(this.selectors.TRIGGER.replace('.', ''));
                }
                item.insertBefore(trigger, item.firstChild);
            }
            const content = this._hydrateContent(item);
            if (content) {
                item.setAttribute('data-expandable', 'item');
                if (this.settings.IGNORE_EMPTY && this._checkIfEmpty(content)) {
                    this.settings.FORCE_HYDRATE_TRIGGER && this._hydrateTrigger(item, true);
                    return;
                }

                this._hydrateTrigger(item);
                if (!item.querySelector(this.selectors.TRIGGER)?.classList.contains(this.classes.DISABLED)) {
                    updatedList.push(item);
                }
            }
        });
        this.selectors.LIST = updatedList;

        if (this.settings.WITH_TOGGLE_ALL && this.selectors.LIST.length > 0 && this.parentWrapper) {
            this._toggleAll();
        }
    }

    /**
     * @param {object} item -  The expandable container
     * @param {object} trigger -  The expandable trigger
     * @param {boolean} forceState -  force open or close the element
     * @param {number|string} speed -  The slideDown animation speed (in case of jQuery)
     */
    toggle(item, trigger, forceState, speed) {
        if (!speed) {
            speed = this.settings.ANIMATION_SPEED;
        }
        this._toggleItem(item, trigger, forceState, speed);
        this.toggleAllTrigger && this._toggleAllHTML();
    }

    _constructArrow() {
        const arrow = document.createElement('i');
        arrow.classList.add(this.classes.CHEVRON);
        arrow.setAttribute('aria-hidden', 'true');
        return arrow;
    }
    /**
     * @param {object} item -  The expandable container
     */
    _hydrateContent(item) {
        const content = item.querySelector(this.selectors.CONTENT) || item;
        if (content) {
            if (this.settings.START_COLLAPSED) {
                if (jQuery) {
                    jQuery(content).hide();
                } else {
                    content.setAttribute('tabindex', '-1');
                }
            }
            content.setAttribute('role', 'region');
            if (!content.id) {
                this.itemIsContent = false;
                content.setAttribute('id', item.id + '_content');
            } else {
                this.itemIsContent = true;
            }
        }
        return content;
    }
    /**
     * @param {object} item -  The expandable trigger
     * @param {boolean} disabled -  if the element is disabled
     */
    _hydrateTrigger(item, disabled) {
        const trigger =
            item.querySelector(this.selectors.TRIGGER) ||
            this.selectors.WRAPPER_CONTAINER?.querySelector("[aria-controls='" + item.id + "']");
        if (trigger) {
            if (!this.itemIsContent) {
                trigger.setAttribute('aria-controls', item.id + '_content');
            }
            trigger.setAttribute('aria-expanded', this.settings.START_COLLAPSED ? 'false' : 'true');
            if (!disabled) {
                trigger.setAttribute('tabindex', '0');
                trigger.setAttribute('role', 'button');
            }

            this._addHeadingLevelToTrigger(trigger);

            trigger.addEventListener('click', () => {
                if (event.target.tagName.toLowerCase() !== 'a') {
                    this.toggle(item, trigger);
                }
            });
            trigger.addEventListener('keydown', e => {
                const tag = event.target.tagName.toLowerCase();
                if (tag === 'a' || tag === 'button') return;

                const code = e.keyCode || e.which;
                if (code === 13) {
                    // enter
                    this.toggle(item, trigger);
                }
            });
        }
        return trigger;
    }

    _addHeadingLevelToTrigger(trigger) {
        if (this.settings.WITH_CHEVRON) {
            const arrow = this._constructArrow();
            trigger.append(arrow);
        }

        if (this.settings.HEADING_LEVEL) {
            const heading = document.createElement(this.settings.HEADING_LEVEL);
            heading.append(...trigger.childNodes);
            trigger.append(heading);
        }
    }

    _toggleAllHTML() {
        const expanded =
            this.expandedList.length > 0 ? this.selectors.LIST.length / 2 < this.expandedList.length : false;
        this.toggleAllTrigger.setAttribute('aria-expanded', expanded);

        const content = this.toggleAllTrigger.querySelector('span');
        (content || this.toggleAllTrigger).innerHTML = this.toggleAllTrigger.getAttribute(
            `data-label-${expanded ? 'collapse' : 'expand'}`
        );

        const icon = this.toggleAllTrigger.querySelector('i');
        if (icon) {
            if (expanded) {
                icon.classList.add(this.classes.EXPANDED_ICON);
                icon.classList.remove(this.classes.COLLAPSED_ICON);
            } else {
                icon.classList.add(this.classes.COLLAPSED_ICON);
                icon.classList.remove(this.classes.EXPANDED_ICON);
            }
        }
    }
    _toggleAll() {
        const btnTmpl = document.getElementById(this.selectors.TOGGLE_TEMPLATE);
        const clone = btnTmpl.content.cloneNode(true);

        if (this.settings.TOGGLE_ALL_POSITION === 'append') {
            this.parentWrapper.appendChild(clone);
        } else {
            this.parentWrapper.prepend(clone);
        }

        this.toggleAllTrigger = this.parentWrapper.querySelector(this.selectors.TOGGLE_ALL);
        const toggle = e => {
            e.preventDefault();
            const expand = /true/.test(e.currentTarget.getAttribute('aria-expanded'));
            this.selectors.LIST.forEach(item => this.toggle(item, undefined, !expand));
        };
        this.toggleAllTrigger.addEventListener('click', toggle);
    }
    /**
     * @param {object} item -  The expandable container
     * @param {object} trigger -  The expandable trigger
     * @param {boolean} forceState -  force open or close the element
     * @param {number|string} speed -  The slideDown animation speed (in case of jQuery)
     */
    _toggleItem(item, trigger, forceState, speed) {
        let contentEl = item?.querySelector(this.selectors.CONTENT) || item;
        let triggerEl = item?.querySelector(this.selectors.TRIGGER) || trigger;

        if (!contentEl) {
            console.debug('No content element found to toggle, trying again...');
            contentEl = document.getElementById(triggerEl.getAttribute('aria-controls'));
        }

        if (!triggerEl) {
            console.debug('No trigger element found to toggle, trying again...');
            triggerEl = this.parentWrapper?.querySelector(['aria-controls="' + contentEl.id + '"']);
        }

        if (!contentEl) return;

        this._updateExpandedList();
        const toExpand = forceState !== undefined ? forceState : /false/.test(triggerEl?.getAttribute('aria-expanded'));
        if (toExpand) {
            if (this.settings.ACCORDION_ENABLED && this.expandedList.length > 0) {
                Array.from(this.expandedList)
                    .filter(el => el.getAttribute('aria-controls') !== contentEl.id)
                    .forEach(el => {
                        this._closeItem(
                            document.getElementById(el.getAttribute('aria-controls')),
                            el,
                            this.settings.ACCORDION_INSTANT_CLOSE_OTHER ? 0 : speed
                        );
                    });
            }
            this._expandItem(contentEl, triggerEl, speed);
        } else {
            this._closeItem(contentEl, triggerEl, speed);
        }
        this._updateExpandedList();
    }

    /**
     * @param {object} item -  The expandable container
     * @param {object} trigger -  The expandable trigger
     * @param {number|string} speed -  The slideUp animation speed (in case of jQuery)
     */
    _closeItem(item, trigger, speed) {
        if (jQuery) {
            jQuery(item).slideUp(speed);
        } else {
            item.setAttribute('tabindex', '-1');
        }
        trigger?.setAttribute('aria-expanded', 'false');
    }
    /**
     * @param {object} item -  The expandable container
     * @param {object} trigger -  The expandable trigger
     * @param {number|string} speed -  The slideDown animation speed (in case of jQuery)
     */
    _expandItem(item, trigger, speed) {
        if (jQuery) {
            jQuery(item).slideDown(speed);
        } else {
            item.setAttribute('tabindex', '0');
        }
        trigger?.setAttribute('aria-expanded', 'true');
    }
    _updateExpandedList() {
        if (this.parentWrapper) {
            this.expandedList = this.parentWrapper.parentNode.querySelectorAll(
                '[data-expandable="item"] [aria-expanded="true"]'
            );
            if (this.expandedList.length === 0 && !this.toggleAllTrigger) {
                this.expandedList = this.parentWrapper.querySelectorAll('[aria-expanded="true"]');
            }
        }
    }

    /**
     * @param {object} element -  the element that we want to check for content. If it's empty we don't need to attach any expandable behavior.
     */
    _checkIfEmpty(element) {
        if (!element) return true;
        const contents = [...element.childNodes].filter(child => {
            const tag = child.tagName ? child.tagName : child.nodeName;
            return tag !== 'H3' && tag !== '#comment';
        });
        return contents.length === 0;
    }
}

module.exports = ExpandableBehavior;
